import { ThemeEvents } from '@theme/events';

class CartCrossSell extends HTMLElement {
  #intersectionObserver;
  #cache = new Map();
  #productIds = [];
  #activated = false;
  #loadToken = 0;

  connectedCallback() {
    this.#productIds = this.#readProductIds();

    this.#intersectionObserver = new IntersectionObserver(
      (entries) => {
        if (!entries[0]?.isIntersecting) return;

        this.#activated = true;
        this.#intersectionObserver?.disconnect();
        this.#loadRecommendations();
      },
      {
        rootMargin: '0px 0px 400px 0px',
      }
    );

    this.#intersectionObserver.observe(this);

    document.addEventListener(
      ThemeEvents.cartUpdate,
      this.#handleCartUpdate
    );

    if (!this.#productIds.length) {
      this.hidden = true;
    }
  }

  disconnectedCallback() {
    this.#intersectionObserver?.disconnect();

    document.removeEventListener(
      ThemeEvents.cartUpdate,
      this.#handleCartUpdate
    );

    this.#loadToken += 1;
  }

  #readProductIds() {
    try {
      const parsed = JSON.parse(this.dataset.productIds || '[]');

      if (!Array.isArray(parsed)) return [];

      return [...new Set(parsed.map((id) => String(id)).filter(Boolean))];
    } catch {
      return [];
    }
  }

  #signature(ids) {
    return [...ids].sort().join(',');
  }

  #handleCartUpdate = async (event) => {
    if (event.detail?.data?.didError) return;

    const newProductIds = await this.#getCurrentCartProductIds(event);

    if (
      this.#signature(newProductIds) ===
      this.#signature(this.#productIds)
    ) {
      return;
    }

    this.#productIds = newProductIds;
    this.dataset.productIds = JSON.stringify(newProductIds);

    if (!newProductIds.length) {
      this.hidden = true;
      this.#loadToken += 1;
      return;
    }

    this.hidden = false;

    if (this.#activated) {
      this.#showLoading();
      this.#loadRecommendations();
    } else {
      this.#intersectionObserver?.observe(this);
    }
  };

  async #getCurrentCartProductIds(event) {
    const eventItems = event.detail?.resource?.items;

    if (Array.isArray(eventItems)) {
      return [
        ...new Set(
          eventItems
            .map((item) => String(item.product_id))
            .filter(Boolean)
        ),
      ];
    }

    try {
      const response = await fetch(this.dataset.cartUrl, {
        headers: {
          Accept: 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Cart request failed: ${response.status}`);
      }

      const cart = await response.json();

      return [
        ...new Set(
          (cart.items || [])
            .map((item) => String(item.product_id))
            .filter(Boolean)
        ),
      ];
    } catch (error) {
      console.error('Cart cross-sell cart error:', error);
      return this.#productIds;
    }
  }

  async #loadRecommendations() {
    if (!this.#productIds.length) {
      this.hidden = true;
      return;
    }

    const token = ++this.#loadToken;

    const maxSources = Number(this.dataset.maxSources || 4);
    const sourceIds = this.#productIds.slice(0, maxSources);

    const results = await Promise.allSettled(
      sourceIds.map((productId) => this.#getCandidates(productId))
    );

    if (token !== this.#loadToken) return;

    const groups = results
      .filter((result) => result.status === 'fulfilled')
      .map((result) => result.value)
      .filter((group) => group.length);

    const merged = this.#mergeCandidates(groups);

    if (!merged.length) {
      this.hidden = true;
      return;
    }

    this.#renderCandidates(merged);
  }

  async #getCandidates(productId) {
    if (this.#cache.has(productId)) {
      return this.#cache.get(productId);
    }

    const url = new URL(
      this.dataset.recommendationsUrl,
      window.location.origin
    );

    url.searchParams.set(
      'limit',
      this.dataset.recommendationsPerSource || '10'
    );
    url.searchParams.set('product_id', productId);
    url.searchParams.set('section_id', this.dataset.sectionId);
    url.searchParams.set('intent', 'related');

    const response = await fetch(url.toString());

    if (!response.ok) {
      throw new Error(
        `Recommendations request failed: ${response.status}`
      );
    }

    const text = await response.text();
    const html = new DOMParser().parseFromString(text, 'text/html');

    const candidates = Array.from(
      html.querySelectorAll('[data-cross-sell-candidate]')
    ).map((candidate) => ({
      id: String(candidate.dataset.productId || ''),
      html: candidate.innerHTML,
    }));

    this.#cache.set(productId, candidates);

    return candidates;
  }

  #mergeCandidates(groups) {
    const maxProducts = Number(this.dataset.maxProducts || 8);
    const excluded = new Set(this.#productIds);
    const seen = new Set();
    const merged = [];

    let index = 0;

    while (merged.length < maxProducts) {
      let foundCandidateAtThisIndex = false;

      for (const group of groups) {
        const candidate = group[index];

        if (!candidate) continue;

        foundCandidateAtThisIndex = true;

        if (!candidate.id) continue;
        if (excluded.has(candidate.id)) continue;
        if (seen.has(candidate.id)) continue;

        seen.add(candidate.id);
        merged.push(candidate);

        if (merged.length >= maxProducts) break;
      }

      if (!foundCandidateAtThisIndex) break;

      index += 1;
    }

    return merged;
  }

  #renderCandidates(candidates) {
    const template = this.querySelector(
      'template[data-cross-sell-template]'
    );
    const output = this.querySelector('[data-cross-sell-output]');
    const loading = this.querySelector('[data-cross-sell-loading]');

    if (!(template instanceof HTMLTemplateElement)) return;
    if (!(output instanceof HTMLElement)) return;

    const fragment = template.content.cloneNode(true);
    const slots = Array.from(
      fragment.querySelectorAll('[data-cross-sell-slot]')
    );

    candidates.forEach((candidate, index) => {
      const slot = slots[index];

      if (!slot) return;

      slot.innerHTML = candidate.html;
    });

    slots.slice(candidates.length).forEach((slot) => {
      const slideshowSlide = slot.closest('slideshow-slide');

      if (slideshowSlide) {
        slideshowSlide.remove();
      } else {
        slot.remove();
      }
    });

    output.replaceChildren(fragment);

    if (loading instanceof HTMLElement) {
      loading.hidden = true;
    }

    this.hidden = false;
  }

  #showLoading() {
    const output = this.querySelector('[data-cross-sell-output]');
    const loading = this.querySelector('[data-cross-sell-loading]');

    if (output instanceof HTMLElement) {
      output.replaceChildren();
    }

    if (loading instanceof HTMLElement) {
      loading.hidden = false;
    }
  }
}

if (!customElements.get('cart-cross-sell')) {
  customElements.define('cart-cross-sell', CartCrossSell);
}